import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Conversation, Message } from "@twilio/conversations";
import { useChatContext } from "../../../context/chatContext";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import { useUnreadStore } from "../store/useOrderStore";
import { ChatType } from "../../../types/chatTypes";
import { CHAT_THEME } from "../../../constants/chatTheme";

type ConversationResponse = {
  conversationSid: string;
  uniqueName: string;
  orcaIdentity: string;
  supplierIdentity?: string;
  customerIdentity?: string;
};

export type UseOrderChatInput = {
  chatType: ChatType;
  orderNo: string;
  orderHandlerUserId: string;
  participantId: string;
  participantName: string;
};

export type UseOrderChatReturn = {
  ready: boolean;
  error: string | null;
  messages: Message[];
  sendMessage: (text: string, files?: File[]) => Promise<void>;
  conversation: Conversation | null;
  isLoading: boolean;
  initializeChat: () => Promise<void>;
};

const SUPPLIER_CONVERSATIONS_ENDPOINT =
  "https://garaaz-chat-9512.twil.io/conversation";
const CUSTOMER_CONVERSATIONS_ENDPOINT =
  "https://garaaz-chat-9512.twil.io/conversation/customer";

export function useOrderChat({
  chatType,
  orderNo,
  orderHandlerUserId,
  participantId,
  participantName,
}: UseOrderChatInput): UseOrderChatReturn {
  const { client, isReady: isClientReady, identity } = useChatContext();
  const userDetails = useUserDetailsContext();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const convRef = useRef<Conversation | null>(null);
  const cleanupFns = useRef<(() => void)[]>([]);
  const initializingRef = useRef(false);

  const cleanup = useCallback(() => {
    cleanupFns.current.forEach((fn) => {
      try {
        fn();
      } catch (err) {
        console.error("Error in cleanup of conversation", err);
      }
    });
    cleanupFns.current = [];
  }, []);

  const initializeChat = useCallback(async () => {
    if (!client || !isClientReady) {
      setError("Twilio Client not ready");
      return;
    }
    if (!orderHandlerUserId || !participantId) {
      console.warn(
        `⚠️ Missing user info: orderHandlerUserId=${orderHandlerUserId}, participantId=${participantId}`
      );
      setError("Missing user info");
      return;
    }
    if (initializingRef.current) return;

    try {
      initializingRef.current = true;
      setError(null);
      setIsLoading(true);
      setReady(false);
      cleanup();

      const uniqueName = `order:${orderNo}|${chatType}:${participantId}`;
      const existingConversation = useUnreadStore
        .getState()
        .conversations.find((c: any) => c?.uniqueName === uniqueName);

      let conv: Conversation;
      if (existingConversation && existingConversation.sid) {
        conv = existingConversation as Conversation;
      } else {
        let endpoint: string;
        let body: any = {
          orderNo,
          handlerUserId: orderHandlerUserId,
          orderHandlerName:
            userDetails?.firstName + " " + userDetails?.lastName,
        };

        switch (chatType) {
          case ChatType.SUPPLIER:
            endpoint = SUPPLIER_CONVERSATIONS_ENDPOINT;
            body = {
              ...body,
              supplierUserId: participantId,
              supplierName: participantName,
              
            };
            break;

          case ChatType.CUSTOMER:
            endpoint = CUSTOMER_CONVERSATIONS_ENDPOINT;
            body = {
              ...body,
              customerId: participantId,
              customerName: participantName,
            };
            break;

          default:
            throw new Error(`Invalid chat type: ${chatType}`);
        }

        const resp = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (!resp.ok) throw new Error(`Ensure failed: ${resp.status}`);
        const data = (await resp.json()) as ConversationResponse;
        conv = await client.getConversationBySid(data.conversationSid);
      }
      const page = await conv.getMessages(100);
      setMessages(page.items);
      setConversation(conv);
      convRef.current = conv;

      setReady(true);
      setIsLoading(false);

      const onAdded = (m: Message) =>
        setMessages((prev) =>
          prev.some((x) => x.sid === m.sid) ? prev : [...prev, m]
        );

      conv.on("messageAdded", onAdded);

      cleanupFns.current.push(() =>
        conv.removeListener("messageAdded", onAdded)
      );
    } catch (e: any) {
      setError(e.message || "Failed to initialize chat");
      setIsLoading(false);
    } finally {
      initializingRef.current = false;
    }
  }, [
    client,
    isClientReady,
    chatType,
    orderNo,
    orderHandlerUserId,
    participantId,
    participantName,
    cleanup,
  ]);

  useEffect(() => {
    return () => cleanup();
  }, [orderNo, cleanup]);

  const sendMessage = useMemo(() => {
    return async (text: string, files?: File[]) => {
      const conv = convRef.current;
      if (!conv) throw new Error("Chat not ready");

      const body = (text || "")
        .trim()
        .slice(0, CHAT_THEME.LIMITS.MAX_MESSAGE_LENGTH);

      if (!body && (!files || files.length === 0)) {
        throw new Error("Message must contain text or attachments");
      }

      try {
        const messageBuilder = conv.prepareMessage();

        if (body) {
          messageBuilder.setBody(body);
        }

        if (files && files.length > 0) {
          for (const file of files) {
            const formData = new FormData();
            formData.set(file.name, file, file.name);
            messageBuilder.addMedia(formData);
          }
        }
        const firstName = userDetails?.firstName?.trim() || "";
        const lastName = userDetails?.lastName?.trim() || "";
        const authorName =
          firstName || lastName
            ? `${firstName} ${lastName}`.trim()
            : "Order Handler";
        messageBuilder.setAttributes({
          authorName: authorName,
        });

        await messageBuilder.build().send();
      } catch (error) {
        console.error("Failed to send message with media:", error);
        throw error;
      }
    };
  }, [userDetails?.firstName, userDetails?.lastName]);

  return {
    ready,
    error,
    messages,
    sendMessage,
    conversation,
    isLoading,
    initializeChat,
  };
}
